import { useEffect, useRef, useState } from 'react';

// =====================================================================
// Autosave de rascunho com debounce + trava de reentrância + baseline.
//
// ⚠️⚠️ MORA EM `src/utils/` DESDE 2026-09-01, e a mudança de casa foi o conserto
//    de uma duplicação: ele existia IDÊNTICO em `manutencao/certificacao/` e em
//    `operacional/bsv/` (só os comentários diferiam), e o Chamados ia ser a
//    TERCEIRA cópia. Três cópias da mesma corrida de gravação é uma que
//    envelhece sozinha — e a que envelhece é justamente a que ninguém está
//    olhando quando o bug aparece.
//
// Consumidores hoje: Certificação (Etapa 1 e 2), Conservação (inspeção),
// Avaliação BSV e Chamados (abertura e finalização).
//
// - DEBOUNCE: dispara `salvarFn` ~`espera`ms depois da última mudança.
// - TRAVA (anti-duplicata): não dispara um novo save enquanto outro está em
//   voo — resolve a corrida da 1ª gravação (em que ainda não há id e dois
//   saves simultâneos criariam dois rascunhos). Se o conteúdo mudou durante o
//   save, re-salva ao terminar.
// - BASELINE: NÃO autosalva logo após abrir/hidratar o formulário. Só salva
//   quando o `snapshot` (string que representa o conteúdo) DIVERGE do último
//   salvo. Enquanto `ativo` é false, a baseline acompanha o snapshot — assim,
//   quando `ativo` vira true (após carregar), o estado carregado já é a base e
//   abrir a tela sozinho não grava nada (importante na Etapa 2: abrir não pode
//   transicionar 'Em diagnóstico' → 'Em execução' sem o usuário editar).
//
// Parâmetros:
//   ativo:    só autosalva quando true (ex.: !carregando && equipamento escolhido).
//   snapshot: string do conteúdo (ex.: JSON.stringify dos campos relevantes).
//   salvarFn: async () => {}  — faz o POST do rascunho; deve lançar em erro.
//   espera:   ms do debounce (padrão 2500).
//
// Retorna { estado, ultimoErro } — estado ∈ 'ocioso'|'salvando'|'salvo'|'erro'.
// =====================================================================
export default function useAutosaveRascunho({ ativo, snapshot, salvarFn, espera = 2500 }) {
  const [estado, setEstado] = useState('ocioso');
  const [ultimoErro, setUltimoErro] = useState('');
  const baseRef = useRef(snapshot);   // último snapshot salvo (ou o de quando ativou)
  const emVoo = useRef(false);        // há um save em andamento?
  const pendente = useRef(false);     // houve mudança durante o save?
  // salvarFn é recriada a cada render (fecha sobre o estado atual); guardamos a
  // mais recente numa ref para o timeout chamar a versão com o estado de agora.
  const salvarRef = useRef(salvarFn);
  salvarRef.current = salvarFn;

  useEffect(() => {
    if (!ativo) { baseRef.current = snapshot; return; } // sem autosave: baseline segue o estado
    if (snapshot === baseRef.current) return;            // nada mudou desde o último save

    const t = setTimeout(async function disparar() {
      if (emVoo.current) { pendente.current = true; return; } // já salvando → marca p/ re-salvar
      emVoo.current = true; pendente.current = false;
      const alvo = snapshot; // snapshot de referência desta gravação
      setEstado('salvando');
      try {
        await salvarRef.current();
        baseRef.current = alvo;
        setEstado('salvo'); setUltimoErro('');
      } catch (e) {
        setEstado('erro'); setUltimoErro(e?.message || 'Erro ao salvar rascunho.');
      } finally {
        emVoo.current = false;
        // Se o usuário mexeu durante o save, agenda outra rodada logo em seguida.
        if (pendente.current) { pendente.current = false; setTimeout(disparar, 300); }
      }
    }, espera);

    return () => clearTimeout(t);
  }, [ativo, snapshot, espera]);

  return { estado, ultimoErro };
}
